// Adapted from T3 Code's contextWindow.ts (MIT, Copyright 2026 T3 Tools Inc.), commit 0ae1091d019484d474fce0fe0a89195a82134041.
// Full notice: public/third-party-licenses.txt.
import type { CodeSessionAgentEvent } from '@/lib/code-sessions/types';
import type { SessionMessageInfo, TokenUsageInfo } from '@opencode/client/promise';

import { eventData } from '@/data/code-sessions/agent-events';

/** OpenCode's measure of a reply's tokens; null for one whose prompt the provider did not measure. */
function measuredTokens(tokens: TokenUsageInfo | undefined): number | null {
  if (tokens === undefined) {
    return null;
  }
  const counts = [
    tokens.input,
    tokens.cache.read,
    tokens.cache.write,
    tokens.output,
    tokens.reasoning,
  ];
  if (!counts.every((count) => Number.isFinite(count))) {
    return null;
  }
  const prompt = tokens.input + tokens.cache.read + tokens.cache.write;
  return prompt > 0 ? prompt + tokens.output + tokens.reasoning : null;
}

/**
 * The tokens a live step's end reports, from event data the page reads unchecked. A count that is
 * missing or not a number makes `measuredTokens` read the reply as unmeasured.
 */
function stepTokens(event: CodeSessionAgentEvent): TokenUsageInfo | undefined {
  const { tokens } = eventData(event);
  const cache = typeof tokens === 'object' && tokens !== null && 'cache' in tokens && tokens.cache;
  return typeof cache === 'object' && cache !== null ? (tokens as TokenUsageInfo) : undefined;
}

/**
 * Tokens the agent's context holds now, by OpenCode's own measure of its latest reply without an
 * error: the prompt the provider counted, cache included, and what the reply wrote. Null when no
 * reply was measured since the conversation was last compacted. Live events win over saved
 * messages: the newest step end or compaction end among `events` decides when there is one.
 */
export function contextTokens(
  messages: readonly SessionMessageInfo[],
  events: readonly CodeSessionAgentEvent[],
): number | null {
  const live = events.findLast(
    (event) =>
      event.type === 'session.compaction.ended' ||
      (event.type === 'session.step.ended' && measuredTokens(stepTokens(event)) !== null),
  );
  if (live !== undefined) {
    return live.type === 'session.step.ended' ? measuredTokens(stepTokens(live)) : null;
  }
  const saved = messages.findLast(
    (message) =>
      (message.type === 'compaction' && message.status === 'completed') ||
      (message.type === 'assistant' &&
        message.error === undefined &&
        measuredTokens(message.tokens) !== null),
  );
  return saved?.type === 'assistant' ? measuredTokens(saved.tokens) : null;
}

/** A token count as the meter shows it: `950`, `1.5k`, `84k`, `1.1m`. */
export function formatContextWindowTokens(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return '0';
  }
  if (value < 1_000) {
    return `${Math.round(value)}`;
  }
  if (value < 10_000) {
    return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  // Anything from 999,500 rounds to 1000k, so it belongs to the next unit.
  if (value < 999_500) {
    return `${Math.round(value / 1_000)}k`;
  }
  return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
}
